import { SharedContextProviders } from '@packages/contexts';
import { FeatureFlagProvider } from '@packages/contexts/feature-flags';
import { LoggingContextBridge } from '@packages/contexts/logging';
import { ContentOverlayProvider } from '@web/core/hooks/use-content-overlay';
import { SummariesProvider } from '@web/core/hooks/use-summaries';
import { AppNavigator } from '@web/core/navigation';
import globalStyles from '@web/jss/global';
import { initI18n } from '@web/utils';
import { useEffect, useRef, type FC } from 'react';
import { ThemeProvider } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';

import { ClickStateProvider } from './core/navigation/navigation.contex';
import { analyticsService } from './services/singletons';
import { useAuthState } from '@packages/contexts/auth';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { useVinSessionState } from './vin-sessions/context/vin-sessions.hooks';
import { GoogleAnalyticsIntegration } from './core/google-analytics/google-analytics-integration';

initI18n();

const useGlobalStyles = createUseStyles(globalStyles);

const queryClient = new QueryClient({ defaultOptions: { mutations: { retry: 3 } } });

const getMaskedAnalyticsPath = (pathname: string): string => {
    const vinPathMatch = pathname.match(/^\/vin\/[^/]+(\/.*)?$/);
    if (!vinPathMatch) {
        return pathname;
    }

    return vinPathMatch[1] || '/';
};

const SpaRouterLogger = () => {
    const { selectedVin } = useVinSessionState();
    const location = useLocation();
    const { status: authStatus } = useAuthState();
    const entryTimeRef = useRef<Date | null>(null);

    const getTargetModule = (pathname: string): string => {
        const vinPathMatch = pathname.match(/^\/vin\/[^/]+\/?(.*)$/);
        if (vinPathMatch) {
            return vinPathMatch[1] || 'vin_session_dashboard';
        }
        return pathname.replace(/\//g, '_').replace(/^_/, '');
    };

    useEffect(() => {
        const now = new Date();
        const durationMs = entryTimeRef.current ? now.getTime() - entryTimeRef.current.getTime() : undefined;

        entryTimeRef.current = now;
        const maskedPathname = getMaskedAnalyticsPath(location.pathname);

        const queryParams = location.search
            ? Object.fromEntries(new URLSearchParams(location.search).entries())
            : undefined;
        const selectedVinValue = selectedVin?.vinRecord?.vin;

        if (selectedVinValue) {
            // only publish for /vin/{vin} or /vin/{vin}/{module} — not deeper nested paths
            const isTopLevelVinPath = /^\/vin\/[^/]+(\/[^/]+)?$/.test(location.pathname);
            if (isTopLevelVinPath) {
                const targetModule = getTargetModule(location.pathname);
                analyticsService.logEvent(ANALYTICS_EVENTS.VIN_SESSION_MODULE_OPENED, {
                    screen: maskedPathname,
                    url: `${maskedPathname}${location.search}${location.hash}`,
                    sourceModule: 'vin_session',
                    targetModule: targetModule,
                    cardType: targetModule,
                    state: authStatus,
                    params: queryParams,
                    duration: durationMs,
                });
            }
        } else {
            analyticsService.logEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
                screen: maskedPathname,
                url: `${maskedPathname}${location.search}${location.hash}`,
                source: document.referrer || undefined,
                state: authStatus,
                params: queryParams,
                duration: durationMs,
            });
        }
    }, [location.pathname]);

    return <></>;
};

const AnalyticsEventDataEnricherBridge = () => {
    const { selectedVin } = useVinSessionState();
    const correlationIdRef = useRef<string>('');
    const previousVinRef = useRef<string | null>(null);

    useEffect(() => {
        analyticsService.setEventDataEnricher((event, data) => {
            const selectedVinValue = selectedVin?.vinRecord?.vin;
            //if (!selectedVinValue || event === ANALYTICS_EVENTS.VIN_SESSION_CLOSED) {
            //    return data;
            //}
            if (data) {
                return {
                    ...data,
                    vin: data?.vin || selectedVinValue,
                    vinSessionState: selectedVinValue ? 'OPENED' : 'CLOSED',
                };
            } else {
                return {
                    vin: selectedVinValue,
                    vinSessionState: selectedVinValue ? 'OPENED' : 'CLOSED',
                };
            }
        });
        return () => {
            analyticsService.setEventDataEnricher(undefined);
        };
    }, [selectedVin]);

    useEffect(() => {
        const currentVin = selectedVin?.vinRecord?.vin || null;
        if (currentVin && currentVin !== previousVinRef.current) {
            // VIN session opened
            const correlationId = crypto.randomUUID();
            correlationIdRef.current = correlationId;
            analyticsService.logEvent(
                ANALYTICS_EVENTS.VIN_SESSION_OPENED,
                {
                    vin: currentVin,
                    correlationId,
                },
                undefined,
                'vin_session'
            );
            previousVinRef.current = currentVin;
        } else if (!currentVin && previousVinRef.current) {
            // VIN session cleared
            analyticsService.logEvent(
                ANALYTICS_EVENTS.VIN_SESSION_CLOSED,
                {
                    correlationId: correlationIdRef.current,
                },
                undefined,
                'stop'
            );
            previousVinRef.current = null;
            correlationIdRef.current = '';
        }
    }, [selectedVin?.vinRecord?.vin]);

    return <></>;
};

const App: FC = () => {
    useGlobalStyles();

    return (
        <QueryClientProvider client={queryClient}>
            <SharedContextProviders>
                <FeatureFlagProvider>
                    <LoggingContextBridge />
                    <ThemeProvider
                        prefixes={{
                            btn: 'subcomm-button',
                            card: 'subcomm-card',
                            'form-label': 'subcomm-form__label',
                            'form-control': 'subcomm-form__control',
                            'form-check': 'subcomm-form__check',
                            'modal-title': 'subcomm-modal__title',
                            'modal-header': 'subcomm-modal__header',
                            'modal-body': 'subcomm-modal__body',
                            'modal-footer': 'subcomm-modal__footer',
                            'dropdown-header': 'subcomm-dropdown__header',
                            'dropdown-divider': 'subcomm-dropdown__divider',
                        }}
                    >
                        <Router>
                            <GoogleAnalyticsIntegration />
                            <SpaRouterLogger />
                            <AnalyticsEventDataEnricherBridge />
                            <SummariesProvider>
                                <ContentOverlayProvider>
                                    <ClickStateProvider>
                                        <AppNavigator />
                                    </ClickStateProvider>
                                </ContentOverlayProvider>
                            </SummariesProvider>
                        </Router>
                    </ThemeProvider>
                </FeatureFlagProvider>
            </SharedContextProviders>
        </QueryClientProvider>
    );
};

export default App;
